import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from './api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
    
    // Listen for session expiry events from the API interceptor
    const handleSessionExpired = (event) => {
      toast.error(event.detail?.message || 'Your session has expired. Please sign in again.');
      logout();
    };
    
    window.addEventListener('auth:session-expired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, []);

  // Check if user is authenticated via cookie
  const checkAuthStatus = async () => {
    try {
      // First try the /auth/check endpoint which reads cookies
      const response = await authAPI.checkAuth();
      
      if (response.data.authenticated) {
        setUser(response.data.user);
        setSessionId(response.data.session_id);
        
        // Store in localStorage for backwards compatibility
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } else {
        // Try localStorage fallback for backwards compatibility
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          try {
            const meResponse = await authAPI.getMe();
            setUser(meResponse.data);
            localStorage.setItem('user', JSON.stringify(meResponse.data));
          } catch (err) {
            // Token invalid, clear it
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Try localStorage fallback
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          localStorage.removeItem('user');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Login - stores token and sets cookies via backend
  const login = useCallback((newToken, userData, newSessionId = null) => {
    // Store token for backwards compatibility with header-based auth
    if (newToken) {
      localStorage.setItem('token', newToken);
    }
    
    // Store user data
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    if (newSessionId) {
      setSessionId(newSessionId);
    }
  }, []);

  // Logout - clears cookies via backend and local storage
  const logout = useCallback(async () => {
    try {
      // Call backend to clear cookies and invalidate session
      await authAPI.logout();
    } catch (error) {
      // Continue with local logout even if backend call fails
      console.error('Logout API call failed:', error);
    }
    
    // Clear local state
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setSessionId(null);
  }, []);

  // Logout from all devices
  const logoutAll = useCallback(async () => {
    try {
      await authAPI.logoutAll();
      toast.success('Logged out from all devices');
    } catch (error) {
      console.error('Logout all failed:', error);
      toast.error('Failed to logout from all devices');
    }
    
    // Clear local state
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setSessionId(null);
  }, []);

  // Refresh user data (useful after profile updates)
  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    logoutAll,
    refreshUser,
    sessionId,
    isAuthenticated: !!user,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
